/**
 * Taste / reference integration.
 * Exactly one cart drawer and one request owner per form. Reference components use
 * ref-* custom elements; the native Taste cart owns quantity changes and checkout.
 */
(() => {
  'use strict';
  if (window.__ehThemeBridgeLoaded) return;
  window.__ehThemeBridgeLoaded = true;
  let refreshPromise;
  let syncingNative = false;
  const drawer = () => document.querySelector('cart-drawer.taste-scope');
  const root = () => window.Shopify?.routes?.root || '/';

  function announce(message) {
    const status = document.getElementById('status-announcement');
    if (status) status.textContent = message || '';
  }

  function updateCount(cart) {
    const count = Number(cart?.item_count || 0);
    document.querySelectorAll('ref-cart-count').forEach(el => { el.itemCount = count; });
    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    let dot = link.querySelector('.eh-cart-dot');
    if (count > 0 && !dot) {
      dot = document.createElement('span');
      dot.className = 'eh-cart-dot';
      dot.setAttribute('aria-hidden', 'true');
      link.append(dot);
    } else if (count === 0) dot?.remove();
  }

  async function getSections() {
    const url = new URL(root(), window.location.origin);
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
    const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('The cart could not be refreshed.');
    return response.json();
  }

  async function refresh({open = false, cart = null, trigger = null} = {}) {
    const el = drawer();
    if (!el) return;
    const wasOpen = el.classList.contains('active');
    if (refreshPromise) {
      await refreshPromise;
      if (open) el.open(trigger || document.getElementById('cart-icon-bubble'));
      return;
    }
    refreshPromise = (async () => {
      let state = cart;
      if (!state) {
        const response = await fetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('The cart could not be loaded.');
        state = await response.json();
      }
      const sections = state.sections?.['cart-drawer'] ? state.sections : await getSections();
      if (typeof sections['cart-drawer'] !== 'string') throw new Error('Missing cart section.');
      el.classList.toggle('is-empty', Number(state.item_count) === 0);
      // renderContents opens the drawer; use it only for deliberate adds or an already-open drawer.
      if (open || wasOpen) {
        el.setActiveElement?.(trigger || document.activeElement);
        el.renderContents({ ...state, sections });
      } else {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const incoming = doc.querySelector('#CartDrawer');
        const existing = el.querySelector('#CartDrawer');
        if (incoming && existing) existing.innerHTML = incoming.innerHTML;
        el.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => el.close());
        if (sections['cart-icon-bubble']) {
          const iconDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          const icon = document.getElementById('cart-icon-bubble');
          const fragment = iconDoc.querySelector('.shopify-section');
          if (icon && fragment) icon.innerHTML = fragment.innerHTML;
        }
      }
      updateCount(state);
    })();
    try { await refreshPromise; }
    catch (error) {
      console.error('[Taste merge]', error);
      announce(error.message);
      if (open) window.location.assign(window.routes?.cart_url || `${root()}cart`);
    } finally { refreshPromise = null; }
  }

  // The reference sends a Set, not an Array. Taste sections stay within Shopify's 5-section bundle limit.
  document.addEventListener('cart:prepare-bundled-sections', event => {
    if (!drawer()) return;
    const sections = event.detail?.sections;
    if (sections?.add) {
      sections.add('cart-drawer');
      sections.add('cart-icon-bubble');
    }
  });
  document.addEventListener('cart:change', event => {
    if (syncingNative || event.detail?.source === 'eh-native-cart') return;
    const cart = event.detail?.cart;
    if (!cart) return;
    updateCount(cart);
    refresh({open: event.detail.baseEvent === 'variant:add', cart});
  });
  document.addEventListener('cart:refresh', () => refresh());

  // Delegation keeps the header cart control functional after theme-editor section reloads.
  document.addEventListener('click', event => {
    const control = event.target.closest?.('[data-taste-cart-open], #cart-icon-bubble');
    if (!control || !drawer() || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    drawer().open(control);
  });
  document.addEventListener('keydown', event => {
    const control = event.target.closest?.('[data-taste-cart-open], #cart-icon-bubble');
    if (!control || !drawer() || event.code !== 'Space') return;
    event.preventDefault();
    drawer().open(control);
  });

  function initialize() {
    document.querySelectorAll('#cart-icon-bubble').forEach(link => {
      link.setAttribute('aria-controls', 'CartDrawer');
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('role', 'button');
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        const cart = event?.cartData;
        if (!cart) return;
        updateCount(cart);
        // Reference caches and optional product recommendations need the current native-cart state.
        syncingNative = true;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true, detail: { source: 'eh-native-cart', baseEvent: 'line-item:change', cart }
        }));
        syncingNative = false;
      });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once: true});
  else initialize();
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('#cart-icon-bubble').forEach(link => {
      link.setAttribute('aria-controls', 'CartDrawer');
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('role', 'button');
    });
  });
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
  window.EHThemeBridge = Object.freeze({ refreshCart: refresh });
})();
